/**
 * Minimal Algolia REST client for the theme.
 *
 * No dependencies and no build step - it just wraps `fetch` around Algolia's
 * multi-query endpoint. Credentials are sent as the documented
 * `x-algolia-application-id` / `x-algolia-api-key` headers.
 *
 * Exposes `theme.algoliaClient`. Every method returns `null` on failure so
 * callers can fall back to Shopify's own search.
 */
(() => {
  const config = (window.theme && window.theme.algolia) || {};

  const MAX_CACHE_SIZE = 30;
  const REQUEST_TIMEOUT = 5000;

  const cache = new Map();

  /**
   * Indices Algolia has told us do not exist. A multi-query fails as a whole if
   * any one index is missing, so once an index 404s it is skipped for the rest
   * of the page's life rather than taking every later request down with it.
   */
  const missingIndices = new Set();

  /**
   * Pulls the index name out of Algolia's "Index X does not exist" message.
   * @param {string} detail - Response body.
   * @returns {string|null}
   */
  function missingIndexFrom(detail) {
    const match = /Index ([\w\-.]+) does not exist/.exec(detail || '');
    return match ? match[1] : null;
  }

  /**
   * A well-formed empty result, used in place of a skipped index so that
   * callers reading results positionally stay aligned.
   * @returns {object}
   */
  function emptyResult() {
    return {
      hits: [], nbHits: 0, page: 0, nbPages: 0, facets: {}
    };
  }

  const AlgoliaClientSorts = {
    /**
     * Shopify sort_by values served straight from the primary index.
     *
     * "manual" is Shopify's curated collection order, which has no Algolia
     * equivalent - a product's position differs per collection and is not
     * indexed. It is served by relevance instead, so that filtering keeps
     * working on collections too large for Shopify's own filters. The curated
     * order still shows on the first, Liquid-rendered page.
     */
    PRIMARY: ['relevance', 'most-relevant', 'manual']
  };

  /**
   * Builds the `params` string for a single Algolia request.
   * @param {object} params - Search parameters, in Algolia's own naming.
   * @returns {string}
   */
  function buildParams(params) {
    const search = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;

      // Array values (facetFilters, numericFilters, facets...) are sent as JSON.
      search.set(key, Array.isArray(value) || typeof value === 'object' ? JSON.stringify(value) : value);
    });

    return search.toString();
  }

  /**
   * Reads a cached response, refreshing its position in the cache.
   * @param {string} key - Cache key.
   * @returns {object|undefined}
   */
  function readCache(key) {
    if (!cache.has(key)) return undefined;

    const value = cache.get(key);
    cache.delete(key);
    cache.set(key, value);
    return value;
  }

  /**
   * Stores a response, evicting the least recently used entry when full.
   * @param {string} key - Cache key.
   * @param {object} value - Response to store.
   */
  function writeCache(key, value) {
    if (cache.size >= MAX_CACHE_SIZE) {
      cache.delete(cache.keys().next().value);
    }
    cache.set(key, value);
  }

  const algoliaClient = {
    /**
     * Whether Algolia is configured and switched on for a given surface.
     * @param {string} [surface] - 'predictive', 'searchPage' or 'collections'.
     * @returns {boolean}
     */
    isEnabled(surface) {
      if (!config.enabled) return false;
      if (!surface) return true;
      return Boolean(config.surfaces && config.surfaces[surface]);
    },

    /**
     * Returns the index name to use for a Shopify `sort_by` value.
     * Returns null when the sort order has no Algolia replica, in which case
     * the caller should fall back to Shopify.
     * @param {string} [sortBy] - Shopify sort_by value.
     * @returns {string|null}
     */
    indexForSort(sortBy) {
      const base = config.indices.products;

      if (!sortBy || AlgoliaClientSorts.PRIMARY.includes(sortBy)) return base;

      const suffix = config.sortReplicas && config.sortReplicas[sortBy];
      return suffix ? `${base}${suffix}` : null;
    },

    /**
     * Runs one or more queries against Algolia.
     * @param {Array} requests - Array of { indexName, params } objects, where
     *   `params` is an object (not yet a string).
     * @param {AbortSignal} [signal] - Optional abort signal.
     * @param {number} [attempt=0] - Internal retry counter for missing indices.
     * @returns {Promise<Array|null>} Array of result objects, or null on failure.
     *   The array always has one entry per request, even for skipped indices.
     */
    async search(requests, signal, attempt = 0) {
      if (!this.isEnabled()) return null;

      // Known-missing indices are left out of the request, but keep their slot
      // in the returned array so callers can keep reading results by position.
      const live = [];
      const slots = requests.map((request) => {
        if (missingIndices.has(request.indexName)) return null;
        live.push(request);
        return live.length - 1;
      });

      if (!live.length) return null;

      const payload = {
        requests: live.map(({ indexName, params }) => ({
          indexName,
          params: buildParams(params || {})
        }))
      };

      const key = JSON.stringify(payload);
      const cached = readCache(key);
      if (cached) return cached;

      const url = `https://${config.appId}-dsn.algolia.net/1/indexes/*/queries`;

      const timeout = new AbortController();
      const timeoutId = setTimeout(() => timeout.abort('Algolia request timed out'), REQUEST_TIMEOUT);

      // Abort when either the caller aborts or the timeout fires.
      if (signal) {
        if (signal.aborted) {
          clearTimeout(timeoutId);
          return null;
        }
        signal.addEventListener('abort', () => timeout.abort(signal.reason), { once: true });
      }

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'x-algolia-application-id': config.appId,
            'x-algolia-api-key': config.searchKey
          },
          body: JSON.stringify(payload),
          signal: timeout.signal
        });

        if (!response.ok) {
          // Algolia returns a JSON body explaining the failure - most usefully
          // "Index ... does not exist", which is what a 404 here normally means.
          const detail = await response.text();
          const missing = missingIndexFrom(detail);

          // The Shopify app only maintains the resources it is configured to
          // index. Rather than lose the whole dropdown because pages or
          // articles are not indexed, drop that index and ask again. One retry
          // per request covers a payload where several are missing.
          if (missing && attempt < requests.length) {
            missingIndices.add(missing);
            return this.search(requests, signal, attempt + 1);
          }

          throw new Error(`Algolia responded with ${response.status}: ${detail}`);
        }

        const { results } = await response.json();
        const merged = slots.map((slot) => (slot === null ? emptyResult() : results[slot]));

        writeCache(key, merged);
        return merged;
      } catch (error) {
        // A caller-triggered abort is expected (the query moved on), anything
        // else is worth surfacing while still falling back to Shopify.
        if (!signal || !signal.aborted) {
          console.warn('Algolia request failed, falling back to Shopify search.', error); // eslint-disable-line no-console
        }
        return null;
      } finally {
        clearTimeout(timeoutId);
      }
    }
  };

  window.theme = window.theme || {};
  window.theme.algoliaClient = algoliaClient;
})();
